<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { SpacingProp } from '@/types/Layout';
import PerfilOficialSwiper from '@/components/perfil-oficial/Swiper.vue';

type SwiperControls = {
  scrollNext: () => void;
  scrollPrev: () => void;
  currentScrollLeft: Ref<number>;
  currentScrollWidth: Ref<number>;
  currentClientWidth: Ref<number>;
};

type Props = {
  /** Modo controlado: dados vêm do parent. undefined → fetch interno. */
  games?: Game[] | null;
  /** Loading externo (modo controlado). */
  externalLoading?: boolean;
  title?: string;
  seeMoreUrl?: string;
  excludeSlug?: string;
  perPage?: number;
  perRow?: SpacingProp;
  spaceBetween?: SpacingProp;
  cardHeight?: string;
};

const props = withDefaults(defineProps<Props>(), {
  games: undefined,
  externalLoading: false,
  title: 'Mais pagou',
  seeMoreUrl: '/games/category/all?initialCategory=mais-pagos',
  excludeSlug: undefined,
  perPage: 10,
  perRow: () => [7, 5, 4.2],
  spaceBetween: () => [16, 12, 8],
  cardHeight: 'auto',
});

const emit = defineEmits<{
  (e: 'seeMore'): void;
}>();

const { getGamesFromCore } = useCasino();
const { getSpacingMeasurementFromPropList } = useLayout();
const { getLoadingStrategy } = useImageLoading();

const swiperRef = ref<
  (InstanceType<typeof PerfilOficialSwiper> & SwiperControls) | null
>(null);

// Modo standalone (sem `games` via prop): faz fetch interno.
// Modo controlado (com `games` via prop): usa direto o que veio.
const internalGames = ref<Game[]>([]);
const internalLoading = ref(false);
const isControlled = computed(() => props.games !== undefined);

async function fetchMaisPagos() {
  internalLoading.value = true;
  try {
    const response = await getGamesFromCore([
      ['sort_by', 'pay_today'],
      ['sort_dir', 'desc'],
      ['per_page', String(props.perPage)],
      ['page', 1],
    ]);

    internalGames.value = (response?.data || []) as unknown as Game[];
  } catch (e) {
    console.error('[MaisPagouApostatudo] erro ao buscar mais pagos:', e);
    internalGames.value = [];
  } finally {
    internalLoading.value = false;
  }
}

onMounted(() => {
  if (!isControlled.value) fetchMaisPagos();
});

const isLoading = computed(() =>
  isControlled.value ? props.externalLoading : internalLoading.value,
);

const sourceGames = computed<Game[]>(() =>
  isControlled.value ? props.games || [] : internalGames.value,
);

const TOP_LIMIT = 10;

const displayGames = computed<Game[]>(() => {
  const filtered = props.excludeSlug
    ? sourceGames.value.filter((g) => g.slug !== props.excludeSlug)
    : sourceGames.value;

  return filtered.slice(0, TOP_LIMIT);
});

const spaceBetweenItems = computed<number>(() =>
  getSpacingMeasurementFromPropList(props.spaceBetween, 12),
);

const spaceBetweenInPixels = computed<string>(
  () => spaceBetweenItems.value + 'px',
);

const itemsPerRow = computed<number>(() =>
  getSpacingMeasurementFromPropList(props.perRow, 7),
);

const skeletonCount = computed<number>(() =>
  Math.max(1, Math.ceil(itemsPerRow.value)),
);

// Enquanto isLoading=true, renderizamos `skeletonCount` placeholders com slug
// próprio. O CasinoMediaApostatudo aceita `:skeleton` e troca apenas o
// CONTEÚDO interno (img → bloco animado, badge → bloco animado), mantendo a
// mesma estrutura externa do swiperItem → swiper não sofre tilt no swap.
const itemsToRender = computed<Game[]>(() => {
  if (isLoading.value) {
    return Array.from({ length: skeletonCount.value }).map(
      (_, i) =>
        ({
          id: `placeholder-${i}`,
          slug: `placeholder-${i}`,
          name: '',
          image: '',
          provider: { id: 0, name: '' },
          pay_today: '0',
          order: null,
          games: undefined,
        }) as unknown as Game,
    );
  }

  return displayGames.value;
});

const mediaItemWidth = computed<string>(() => {
  const gapValue = spaceBetweenItems.value;
  const items = itemsPerRow.value;

  return `calc((100% - ${(items - 1) * gapValue}px) / ${items})`;
});
</script>

<template>
  <div :class="$style.maisPagou">
    <!-- Header customizado com SVG inline -->
    <div :class="$style.header">
      <div :class="$style.titleContainer">
        <span :class="$style.headerIcon">
          <svg
            fill="none"
            height="31"
            viewBox="0 0 32 31"
            width="32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.13184 0.510742H25.5498C28.654 0.510933 31.1699 3.02764 31.1699 6.13184V24.5273C31.1699 27.6316 28.654 30.1482 25.5498 30.1484H6.13184C3.02752 30.1484 0.510742 27.6317 0.510742 24.5273V6.13184C0.510757 3.02753 3.02753 0.51076 6.13184 0.510742Z"
              stroke="white"
              stroke-opacity="0.1"
              stroke-width="1.02198"
            />
            <path
              clip-rule="evenodd"
              d="M15.3305 10.8339L14.9458 10.5616C14.3967 10.173 13.7406 9.96436 13.068 9.96436H12.5985C12.2887 9.96436 11.9758 9.98389 11.6838 10.0874C9.8575 10.7352 8.60126 13.1031 8.69213 18.4743C8.71043 19.5565 8.96366 20.7526 9.94011 21.2196C10.2444 21.3651 10.6012 21.4616 11.0074 21.4616C11.4949 21.4616 11.8821 21.3226 12.1809 21.1268C12.4942 20.9214 12.77 20.6545 13.0458 20.3877C13.3866 20.0581 13.7272 19.7285 14.1382 19.5154C14.5857 19.2833 15.0825 19.1622 15.5867 19.1622H17.1179C17.6221 19.1622 18.1189 19.2833 18.5665 19.5154C18.9775 19.7285 19.3181 20.0581 19.6588 20.3877C19.9346 20.6545 20.2105 20.9214 20.5238 21.1268C20.8226 21.3226 21.2098 21.4616 21.6972 21.4616C22.1035 21.4616 22.4603 21.3651 22.7646 21.2196C23.741 20.7526 23.9942 19.5565 24.0125 18.4743C24.1034 13.1031 22.8472 10.7352 21.0209 10.0874C20.7289 9.98389 20.4159 9.96436 20.1061 9.96436H19.6367C18.964 9.96436 18.3079 10.173 17.7589 10.5616L17.3742 10.8339C17.0754 11.0453 16.7184 11.1589 16.3523 11.1589C15.9863 11.1589 15.6293 11.0453 15.3305 10.8339ZM19.9931 13.0303C20.3106 13.0303 20.568 13.2877 20.568 13.6052C20.568 13.9226 20.3106 14.18 19.9931 14.18C19.6756 14.18 19.4183 13.9226 19.4183 13.6052C19.4183 13.2877 19.6756 13.0303 19.9931 13.0303ZM12.9032 13.2219C13.2206 13.2219 13.478 13.4793 13.478 13.7968V14.3716H14.0529C14.3704 14.3716 14.6277 14.629 14.6277 14.9465C14.6277 15.264 14.3704 15.5214 14.0529 15.5214H13.478V16.0962C13.478 16.4137 13.2206 16.6711 12.9032 16.6711C12.5857 16.6711 12.3283 16.4137 12.3283 16.0962V15.5214H11.7534C11.4359 15.5214 11.1786 15.264 11.1786 14.9465C11.1786 14.629 11.4359 14.3716 11.7534 14.3716H12.3283V13.7968C12.3283 13.4793 12.5857 13.2219 12.9032 13.2219ZM21.7177 14.7549C21.7177 15.0724 21.4603 15.3297 21.1429 15.3297C20.8254 15.3297 20.568 15.0724 20.568 14.7549C20.568 14.4374 20.8254 14.18 21.1429 14.18C21.4603 14.18 21.7177 14.4374 21.7177 14.7549ZM18.8434 15.3297C19.1609 15.3297 19.4183 15.0724 19.4183 14.7549C19.4183 14.4374 19.1609 14.18 18.8434 14.18C18.5259 14.18 18.2685 14.4374 18.2685 14.7549C18.2685 15.0724 18.5259 15.3297 18.8434 15.3297ZM20.568 15.9046C20.568 15.5871 20.3106 15.3297 19.9931 15.3297C19.6756 15.3297 19.4183 15.5871 19.4183 15.9046C19.4183 16.2221 19.6756 16.4795 19.9931 16.4795C20.3106 16.4795 20.568 16.2221 20.568 15.9046Z"
              fill="white"
              fill-rule="evenodd"
            />
          </svg>
        </span>
        <p :class="$style.titleText">{{ title }}</p>
      </div>

      <NuxtLink
        v-if="seeMoreUrl"
        :class="$style.seeMore"
        :draggable="false"
        no-prefetch
        :to="seeMoreUrl"
        @click="emit('seeMore')"
      >
        VER MAIS
      </NuxtLink>
    </div>

    <!-- Swiper SEMPRE montado: trocamos só os filhos (skeleton ↔ card real)
         pra não desmontar/remontar a estrutura no swap, evitando flash de
         "encolhida" entre estados. -->
    <PerfilOficialSwiper
      v-if="isLoading || displayGames.length"
      ref="swiperRef"
      :card-height="cardHeight"
      :gap="spaceBetweenInPixels"
    >
      <template #default="{ handleClick }">
        <div
          v-for="(game, index) in itemsToRender"
          :key="index"
          :class="$style.swiperItem"
        >
          <CasinoMediaApostatudo
            :class="$style.mediaCard"
            :game="game"
            :index="index"
            :loading="getLoadingStrategy(index, 0)"
            show-pay-today
            :skeleton="isLoading"
            type="game"
            @click="handleClick"
          />
        </div>
      </template>
    </PerfilOficialSwiper>
  </div>
</template>

<style module lang="postcss">
.maisPagou {
  @apply flex flex-col select-none w-full mb-5 md:mb-7;
}

.header {
  @apply flex justify-between items-center rounded-md mb-2.5 lg:mb-4 pr-[10px];
  @apply [background:linear-gradient(270deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.00)_100%)];
  user-select: none;
}

.titleContainer {
  @apply flex items-center h-[31px] lg:h-[45px] gap-2.5 md:gap-2;
}

.headerIcon {
  @apply flex-shrink-0 flex items-center justify-center;
  width: 32px;
  height: 31px;
}

.headerIcon svg {
  width: 100%;
  height: 100%;
  display: block;
}

.titleText {
  @apply text-white text-[16px] lg:text-[20px] font-bold leading-4;
}

.seeMore {
  @apply flex items-center justify-center ml-auto gap-1 px-2.5 text-white text-[11px] font-bold leading-4;
  border-radius: 5.11px;
  border: 1.022px solid rgba(255, 255, 255, 0.1);
  padding: 2.044px 7.154px;
}

.swiperItem {
  min-width: v-bind(mediaItemWidth);
  width: v-bind(mediaItemWidth);
  flex-shrink: 0;
  height: auto;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  /* Lock mínimo pra evitar collapse durante swap skeleton↔card.
     Cobre cardWidth × 1.16 + 4 (gap) + 40 (badge) ≈ 130-280 por breakpoint. */
  min-height: 140px;
}

@media (min-width: 768px) {
  .swiperItem {
    min-height: 200px;
  }
}

@media (min-width: 1200px) {
  .swiperItem {
    min-height: 290px;
  }
}

.mediaCard {
  @apply relative;
  z-index: 1;
  width: 100%;
  max-width: none;
}

/* Skeleton replica EXATAMENTE a estrutura do MediaApostatudo:
   - imageWrapper: pt-[116%] (aspect ratio fixo 1:1.16)
   - payTodayBadge: mt-1 (4px), height 40px / 44px md / 48px lg
   Skeletons usam .swiperItem (mesma classe dos cards reais) → swap não muda
   estrutura externa, só o conteúdo interno troca (skeleton ↔ MediaApostatudo). */
/* padding-top: 116% replica EXATAMENTE o pt-[116%] do imageWrapper real
   no MediaApostatudo. Mais robusto cross-browser que aspect-ratio. */
.skeletonImage {
  width: 100%;
  height: 0;
  padding-top: 116%;
  border-radius: 6px;
}

.skeletonBadge {
  width: 100%;
  height: 40px;
  margin-top: 4px;
  border-radius: 6px;
}

@media (min-width: 768px) {
  .skeletonBadge {
    height: 44px;
  }
}

@media (min-width: 1200px) {
  .skeletonBadge {
    height: 48px;
  }
}
</style>
